/**
 * FIX-2 (prod fixes 2026-10-02, UX-SPEC §2): expired uploads -- refuse early, ask to re-upload.
 *
 * Re-edit, Apply & Restart or Re-run on an upload the edge has already deleted created a job that failed ~99% of the
 * time (6-24 h cross-edge 98% failed, >= 24 h 100%; `prod-audit-20261002/funnel/out-uploads-7d.txt`). The server half
 * (T2) refuses such a schedule with HTTP 410 `{code:"UPLOAD_EXPIRED"}` and answers `POST /v1/api/uploads/availability`;
 * this module is the web half's PURE core: which cards are past the retention limit, which Re-edit needs to ask first,
 * how a 410 is recognised, and the one-shot "Upload it again" handoff. No container, no React -- it sits in the tool
 * page's import graph (`file-convert.component.tsx`), where a container edge has crashed the webpack build before.
 *
 * Flag `uploadExpiryGate` off (or a server that does not send it) = every predicate here says "no", so no availability
 * call is made and no new string can render.
 */
import type { OriginalInputFile } from '../types/types';

/** UX-SPEC §13 copy deck, IDs X2-1..X2-7. Never an exact number of hours, never a countdown (§2.2, §2.4). */
export const X2_COPY = {
    /** X2-1: the card's expired line (`processing-notes`, `data-jkey="reedit-expired"`, role=status). */
    expiredLine: 'This file is no longer on our servers — uploads are deleted after a few hours to protect your privacy.',
    /** X2-2: the action next to X2-1, and the modal's primary after a 410. */
    uploadAgain: 'Upload it again',
    /** X2-3: the Re-edit modal's inline alert after a 410 (Apply & Restart is removed). */
    modalAlert: 'This file is no longer on our servers, so it can\'t be run again. Upload it again to use these settings.',
    /** X2-4: tool-page staged row when a silent re-upload is not possible. */
    stagedBlocked: 'This upload expired. Remove it and add the file again.',
    /** X2-5: tool-page staged row during the silent re-upload. */
    stagedReuploading: 'Uploading again…',
    /** X2-6: tool page, near the options, after "Upload it again" carried the card's settings. */
    settingsKept: 'Your settings from last time are selected.',
    /** X2-7: the Re-edit key while the availability check runs. */
    checking: 'Checking…',
    /** The modal's secondary after a 410 (UX-SPEC §2.2 X2-3 "Buttons: Upload it again (primary), Close"). */
    close: 'Close',
} as const;

/** C3 defaults (`DiskCleanup.DEFAULT_MAX_FILE_AGE_MINUTES` = 480; check after 60). Used only when the server omits them. */
export const DEFAULT_UPLOAD_RETENTION_MINUTES = 480;
export const DEFAULT_UPLOAD_CHECK_AFTER_MINUTES = 60;
/** UX-SPEC §2.1 T2: the Re-edit key waits at most 3 s for the availability answer, then opens the modal anyway. */
export const AVAILABILITY_TIMEOUT_MS = 3000;
/** The C3 endpoint takes at most 20 inputs per call. */
export const AVAILABILITY_MAX_INPUTS = 20;

export interface UploadExpiryConfig {
    /** `featureFlags.uploadExpiryGate === true` -- strictly; absent (an older server) reads off. */
    enabled: boolean;
    retentionMinutes: number;
    checkAfterMinutes: number;
}

export const UPLOAD_EXPIRY_OFF: UploadExpiryConfig = Object.freeze({
    enabled: false,
    retentionMinutes: DEFAULT_UPLOAD_RETENTION_MINUTES,
    checkAfterMinutes: DEFAULT_UPLOAD_CHECK_AFTER_MINUTES,
});

function positiveMinutes(...candidates: unknown[]): number | null {
    for (const c of candidates) {
        const n = typeof c === 'string' && c.trim() !== '' ? Number(c) : c;
        if (typeof n === 'number' && Number.isFinite(n) && n > 0) return n;
    }
    return null;
}

/**
 * The two public minute values from the last settings response's flat fields (C3), recorded by `SettingsService` as it
 * splits the response -- BEFORE it emits the flags -- so a flag subscriber reads them without a second subscription
 * (cards read the top-level flags only; `file-list-item.component.spec.tsx` pins that).
 */
let lastSettingsMinutes: { uploadRetentionMinutes?: unknown; uploadCheckAfterMinutes?: unknown } = {};
export function rememberUploadExpirySettings(settings: unknown): void {
    try {
        const s = (settings && typeof settings === 'object' ? settings : {}) as Record<string, unknown>;
        lastSettingsMinutes = { uploadRetentionMinutes: s.uploadRetentionMinutes, uploadCheckAfterMinutes: s.uploadCheckAfterMinutes };
    } catch {
        lastSettingsMinutes = {};
    }
}

/**
 * The gate's settings as the browser sees them. The kill switch lives in `featureFlags`; the two minute values are
 * public settings (C3) and are read from either place the settings response can carry them (beside the flags, or the
 * flat fields `rememberUploadExpirySettings` recorded), so the web does not depend on which projection T1 picked.
 * Never throws.
 */
export function uploadExpiryConfigOf(flags: unknown, settings: unknown = lastSettingsMinutes): UploadExpiryConfig {
    try {
        const f = (flags && typeof flags === 'object' ? flags : {}) as Record<string, unknown>;
        const s = (settings && typeof settings === 'object' ? settings : {}) as Record<string, unknown>;
        const retentionMinutes = positiveMinutes(f.uploadRetentionMinutes, s.uploadRetentionMinutes) ?? DEFAULT_UPLOAD_RETENTION_MINUTES;
        const checkAfter = positiveMinutes(f.uploadCheckAfterMinutes, s.uploadCheckAfterMinutes) ?? DEFAULT_UPLOAD_CHECK_AFTER_MINUTES;
        return {
            enabled: f.uploadExpiryGate === true,
            retentionMinutes,
            checkAfterMinutes: Math.min(checkAfter, retentionMinutes),
        };
    } catch {
        return UPLOAD_EXPIRY_OFF;
    }
}

/** The upload row id in an input's stored name (`<random>-x-<24 hex ObjectId>.<ext>`, `FileNameGenerator`). */
const INPUT_OBJECT_ID = /-x-([0-9a-f]{24})(?:-[^.]*)?(?:\.[^.]*)?$/i;

/**
 * When an input was uploaded: the timestamp inside its upload row's ObjectId (the row is minted when the upload starts,
 * `FileUploadUtils.newFileId`). The card's own `uploadStartTime` is the moment Convert was pressed -- for a re-edited card
 * that is the RE-EDIT, hours after the bytes arrived -- so it is only the fallback. Null when unreadable.
 */
export function uploadTimeMsOfInput(input: Partial<OriginalInputFile> | null | undefined): number | null {
    const location = typeof input?.location === 'string' ? input.location : '';
    const m = INPUT_OBJECT_ID.exec(location);
    if (!m) return null;
    const seconds = parseInt(m[1].slice(0, 8), 16);
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
}

interface ExpiryCardFacts {
    originalInputFiles?: Partial<OriginalInputFile>[] | null;
    uploadStartTime?: Date | string | number | null;
    processEndTime?: Date | string | number | null;
    result?: { end?: unknown; error?: boolean; failureCode?: string; failureDetailCode?: string; sourceGone?: boolean } | null;
}

function timeMs(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const t = value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(String(value));
    return Number.isFinite(t) && t > 0 ? t : null;
}

/**
 * Minutes since the OLDEST input of this card was uploaded (a merge is gone as soon as one input is), or null when no
 * time is known. Never negative (a skewed clock reads 0).
 */
export function uploadAgeMinutes(card: ExpiryCardFacts | null | undefined, now: number = Date.now()): number | null {
    if (!card) return null;
    const inputs = Array.isArray(card.originalInputFiles) ? card.originalInputFiles : [];
    const times = inputs.map(uploadTimeMsOfInput).filter((t): t is number => t !== null);
    const oldest = times.length > 0 ? Math.min(...times) : timeMs(card.uploadStartTime);
    if (oldest === null) return null;
    return Math.max(0, (now - oldest) / 60000);
}

/**
 * From what age the browser decides "gone" ON ITS OWN, with no server call (integration round 2, INT1-P3-01): twice the
 * retention limit (16 h at the default 480 min), not the limit itself. The age here is the input's ObjectId time, i.e.
 * the upload START, read against this browser's clock, while the edge deletes by the file's mtime and the server
 * (`UploadExpiryChecker`) by `completedDate`. Between the limit and this point the three can disagree and the file can
 * still exist: a long chunked upload (finished an hour or more after its id), an input a frame picker or Studio session
 * kept alive (the server asks the edge for those), or a client clock running fast. So in that band the card keeps
 * Re-edit / Make it smaller / its Re-run row and the server decides on press ([needsAvailabilityCheck]: one
 * availability call, which answers `missing` without an edge call when nothing kept the upload alive).
 */
export function expiredOnLoadAfterMinutes(cfg: UploadExpiryConfig): number {
    return 2 * cfg.retentionMinutes;
}

/**
 * T1: the upload is certainly past the retention limit ([expiredOnLoadAfterMinutes]) -- the card shows the expired
 * state on load, no click needed.
 */
export function isPastRetention(card: ExpiryCardFacts | null | undefined, cfg: UploadExpiryConfig, now: number = Date.now()): boolean {
    if (!cfg?.enabled) return false;
    const age = uploadAgeMinutes(card, now);
    return age !== null && age >= expiredOnLoadAfterMinutes(cfg);
}

/**
 * T2: Re-edit must ask the server first -- the upload is at least `checkAfterMinutes` old and not yet expired on load
 * (past [expiredOnLoadAfterMinutes], T1 already shows the expired state). That includes the band just past the
 * retention limit, where only the server can tell (INT1-P3-01). A card of unknown age is not checked: the submit-time
 * refusal (T3) is the backstop, and "uploads younger than uploadCheckAfterMinutes never call availability" must hold.
 */
export function needsAvailabilityCheck(card: ExpiryCardFacts | null | undefined, cfg: UploadExpiryConfig, now: number = Date.now()): boolean {
    if (!cfg?.enabled) return false;
    const age = uploadAgeMinutes(card, now);
    return age !== null && age >= cfg.checkAfterMinutes && age < expiredOnLoadAfterMinutes(cfg);
}

/**
 * T4: a failed card whose input was gone when the job ran -- `SOURCE_EXPIRED`, or a `TRANSFER_FAILED` the server marks as
 * source-gone (`failureDetailCode` SOURCE_GONE / SOURCE_EXPIRED, or `sourceGone: true`). Same expired state as T1.
 */
export function isSourceGoneFailure(result: ExpiryCardFacts['result']): boolean {
    if (!result || result.error !== true) return false;
    const code = `${result.failureCode || ''}`.toUpperCase();
    if (code === 'SOURCE_EXPIRED') return true;
    if (code !== 'TRANSFER_FAILED') return false;
    const detail = `${result.failureDetailCode || ''}`.toUpperCase();
    return result.sourceGone === true || detail === 'SOURCE_GONE' || detail === 'SOURCE_EXPIRED';
}

/**
 * Download stays on an expired card only while its output may still exist: outputs age out on the same clock (UX-SPEC
 * §2.6). True only when the job's end is KNOWN and at least the retention limit ago; an unknown end keeps Download.
 */
export function outputKnownGone(card: ExpiryCardFacts | null | undefined, cfg: UploadExpiryConfig, now: number = Date.now()): boolean {
    if (!card || !cfg?.enabled) return false;
    const ended = timeMs(card.processEndTime) ?? timeMs(card.result?.end);
    return ended !== null && (now - ended) / 60000 >= cfg.retentionMinutes;
}

/** The C3 request body's inputs: the stored name and its upload token (never put in a URL, never logged). */
export function availabilityInputsOf(card: ExpiryCardFacts | null | undefined): { name: string; token?: string }[] {
    const inputs = Array.isArray(card?.originalInputFiles) ? card.originalInputFiles : [];
    return inputs
        .filter(i => typeof i?.location === 'string' && i.location !== '')
        .slice(0, AVAILABILITY_MAX_INPUTS)
        .map(i => (i.token ? { name: i.location as string, token: i.token } : { name: i.location as string }));
}

export type AvailabilityOutcome = 'present' | 'missing' | 'check_failed';

/**
 * The C3 answer, reduced to the three outcomes the UX knows. ANY input missing -> missing. Every input present ->
 * present. Anything else (`unknown`, a malformed body, fewer results than inputs) -> check_failed, which opens the
 * modal: we never block on our own uncertainty (§2.1 T2).
 */
export function availabilityOutcomeOf(body: unknown, expected: number): AvailabilityOutcome {
    const results = (body as { results?: unknown })?.results;
    if (!Array.isArray(results)) return 'check_failed';
    const states = results.map(r => `${(r as { state?: unknown })?.state ?? ''}`.toLowerCase());
    if (states.includes('missing')) return 'missing';
    if (states.length >= expected && expected > 0 && states.every(s => s === 'present')) return 'present';
    return 'check_failed';
}

/** The schedule refusal's body (C2), parsed defensively: the HTTP layer may hand it over as a string. */
function bodyOf(err: unknown): Record<string, unknown> | null {
    const raw = (err as { response?: unknown })?.response;
    if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
            return null;
        }
    }
    return null;
}

/** C2: HTTP 410 `{code:"UPLOAD_EXPIRED"}` -- the server refused BEFORE creating a workflow. Nothing else matches. */
export function isUploadExpiredError(err: unknown): boolean {
    if (!err) return false;
    if ((err as { uploadExpired?: unknown }).uploadExpired === true) return true;
    if ((err as { status?: unknown }).status !== 410) return false;
    return `${bodyOf(err)?.code ?? ''}` === 'UPLOAD_EXPIRED';
}

/** C2 `inputs` (1-based) -> 0-based indexes into the card's inputs; empty when the server named none (= all of them). */
export function expiredInputIndexes(err: unknown): number[] {
    const inputs = bodyOf(err)?.inputs;
    if (!Array.isArray(inputs)) return [];
    return inputs
        .map(n => Number(n))
        .filter(n => Number.isInteger(n) && n >= 1)
        .map(n => n - 1);
}

// ---- "Upload it again" (UX-SPEC §2.2): the card's tool page, the card's settings, one shot ----------------------------

/** sessionStorage key: written by the card, consumed (read AND removed) once by the tool page it names. */
export const UPLOAD_AGAIN_KEY = 'xc-upload-again';
/** A handoff older than this is ignored (and still removed): it belongs to a click the user has moved on from. */
export const UPLOAD_AGAIN_MAX_AGE_MS = 10 * 60 * 1000;

/** Keys of the posted summary that describe the OLD files, not the user's settings. */
const PER_FILE_OPTION_KEYS = ['orientationAngleList', 'orientationFlipList', 'uploadedFileExtensions'];

export interface UploadAgainHandoff {
    v: 1;
    slug: string;
    at: number;
    options: Record<string, unknown>;
    /** Name + size of each input, to find the file again in this browser's recent files (`transfer-tray` session store). */
    files: { name: string; size: number }[];
}

interface StorageLike {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

function sessionStore(): StorageLike | null {
    try {
        return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
    } catch {
        return null;
    }
}

function slugOf(card: { pageData?: { path?: unknown } | null; uniqueProcessType?: unknown } | null | undefined): string | null {
    const slug = card?.pageData?.path || card?.uniqueProcessType;
    return typeof slug === 'string' && slug.replace(/^\/+/, '') !== '' ? slug.replace(/^\/+/, '') : null;
}

/**
 * Where "Upload it again" goes: the card's OWN tool page (`cardSlugOf`'s rule), never a generic page. Writes the
 * one-shot handoff first; returns null (no button) when the card names no page. Never throws -- a storage failure
 * still navigates, just without the settings.
 */
export function prepareUploadAgain(
    card: {
        pageData?: { path?: unknown } | null; uniqueProcessType?: unknown; options?: unknown;
        originalInputFiles?: { metadata?: { fname?: unknown; fsize?: unknown } | null; originalName?: unknown }[] | null;
    } | null | undefined,
    now: number = Date.now(),
    storage: StorageLike | null = sessionStore(),
): string | null {
    const slug = slugOf(card);
    if (!slug) return null;
    try {
        const options: Record<string, unknown> = {};
        const raw = card?.options && typeof card.options === 'object' ? card.options as Record<string, unknown> : {};
        Object.keys(raw).forEach(k => { if (!PER_FILE_OPTION_KEYS.includes(k)) options[k] = raw[k]; });
        const files = (Array.isArray(card?.originalInputFiles) ? card.originalInputFiles : [])
            .map(i => ({ name: `${i?.metadata?.fname ?? i?.originalName ?? ''}`, size: Number(i?.metadata?.fsize) }))
            .filter(f => f.name !== '' && Number.isFinite(f.size) && f.size > 0);
        const handoff: UploadAgainHandoff = { v: 1, slug, at: now, options, files };
        storage?.setItem(UPLOAD_AGAIN_KEY, JSON.stringify(handoff));
    } catch { /* navigate anyway */ }
    return `/${slug}`;
}

/** Window event: "Upload it again" was pressed on the tool page it names (the Re-run row's modal), so no route change. */
export const UPLOAD_AGAIN_EVENT = 'xc-upload-again';

/**
 * Go to "Upload it again"'s page. When it IS the page already showing (the Re-edit modal opened from the tool page's
 * Re-run row), a push to the same route does not remount the page, so the handoff would sit unread: the page is told
 * through `UPLOAD_AGAIN_EVENT` instead and consumes it at once. Never throws.
 */
export function goToUploadAgain(href: string, push: (href: string) => Promise<unknown>): void {
    if (typeof window === 'undefined') return;
    try {
        const here = `${window.location.pathname || ''}`.replace(/\/+$/, '');
        if (here !== '' && here === href.replace(/\/+$/, '')) {
            window.dispatchEvent(new Event(UPLOAD_AGAIN_EVENT));
            return;
        }
    } catch { /* fall through to the navigation */ }
    try {
        push(href).catch(() => window.location.assign(href));
    } catch {
        window.location.assign(href);
    }
}

/**
 * The tool page's side: read the handoff ONCE. It is removed whatever it says (consumed even when it is stale, malformed
 * or for another page), so a reload or a later visit never re-applies it. Returns it only for THIS page and only fresh.
 */
export function consumeUploadAgain(pageSlug: string | null | undefined, now: number = Date.now(), storage: StorageLike | null = sessionStore()): UploadAgainHandoff | null {
    if (!storage) return null;
    let raw: string | null = null;
    try {
        raw = storage.getItem(UPLOAD_AGAIN_KEY);
        if (raw === null) return null;
        storage.removeItem(UPLOAD_AGAIN_KEY);
    } catch {
        return null;
    }
    try {
        const h = JSON.parse(raw) as UploadAgainHandoff;
        const page = `${pageSlug ?? ''}`.replace(/^\/+/, '');
        if (!h || h.v !== 1 || !page || h.slug !== page) return null;
        if (typeof h.at !== 'number' || now - h.at > UPLOAD_AGAIN_MAX_AGE_MS || now - h.at < -60000) return null;
        if (!h.options || typeof h.options !== 'object') h.options = {};
        if (!Array.isArray(h.files)) h.files = [];
        return h;
    } catch {
        return null;
    }
}

/**
 * The recent files (`transfer-tray` session records) that are the handoff's inputs, matched by name + size, in input
 * order. ALL or nothing: staging half of a merge's inputs would run a different job than the one the card describes.
 */
export function matchRecentFiles<R extends { name: string; size: number }>(handoff: UploadAgainHandoff | null, recent: R[] | null | undefined): R[] {
    if (!handoff || !Array.isArray(recent) || handoff.files.length === 0) return [];
    const out: R[] = [];
    for (const f of handoff.files) {
        const hit = recent.find(r => r && r.name === f.name && r.size === f.size && !out.includes(r));
        if (!hit) return [];
        out.push(hit);
    }
    return out;
}

/**
 * Tool page, after a 410 (UX-SPEC §2.1 T3): which refused cards' staged rows are uploaded again silently (X2-5), and which
 * rows are `blocked` (X2-4). A card is retried only when EVERY row it was made from still holds the user's bytes and none
 * was already re-uploaded this visit ("schedule once" -- a second refusal never loops). Pure; the page does the I/O.
 */
export function planExpiredRetry<R>(groups: R[][], opts: { retried(row: R): boolean; hasBytes(row: R): boolean }): { retry: R[][]; blocked: R[] } {
    const retry: R[][] = [];
    const blocked: R[] = [];
    for (const group of groups || []) {
        const rows = (group || []).filter(Boolean);
        if (rows.length === 0) continue;
        if (rows.every(r => !opts.retried(r) && opts.hasBytes(r))) retry.push(rows);
        else blocked.push(...rows);
    }
    return { retry, blocked };
}

/** Does this staged row still hold the user's bytes (a local pick), i.e. can it be uploaded again without asking? */
export function rowHoldsBytes(row: { file?: unknown } | null | undefined): boolean {
    const f = row?.file;
    return typeof File !== 'undefined' && f instanceof File && f.size > 0;
}
